<template>
    <div>
        <!-- file list -->
        <div :class="['mb-4 rounded-md']">
            <div class="flex max-h-64 flex-col gap-2 overflow-y-auto">
                <div
                    v-for="file in files"
                    :key="file.id"
                    class="bg-muted flex items-center gap-3 rounded-md px-3 py-3">
                    <div
                        class="bg-background text-muted-foreground flex size-12 flex-shrink-0 items-center justify-center rounded text-xs font-semibold uppercase">
                        {{ file.extension }}
                    </div>
                    <div class="flex flex-grow flex-col">
                        <p class="truncate text-sm font-semibold">{{ file.name }}</p>
                        <p class="text-muted-foreground truncate text-xs">
                            {{ file.sizeLabel }}
                        </p>
                    </div>
                    <Button
                        @click="downloadSingleFile(file.id)"
                        :disabled="isTurnstileTokenLoaded"
                        variant="outline"
                        class="bg-background"
                        size="sm">
                        Download
                    </Button>
                </div>
            </div>

            <slot></slot>
        </div>

        <div class="grid gap-4">
            <div v-if="isDownloadStarted" class="bg-muted rounded-md p-3 text-center text-sm">
                Your download has started. <br />Download not starting? Click on the button below
                again
            </div>
            <Button @click="downloadAllFiles" :disabled="isTurnstileTokenLoaded" variant="yellow">
                Download
            </Button>
        </div>

        <!-- The password dialog will only be shown once. After a correct password is captured, it will stop showing. -->
        <Dialog :open="showPasswordDialog" @update:open="showPasswordDialog = $event">
            <DialogContent class="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Password Required</DialogTitle>
                    <DialogDescription>
                        Please enter the password provided by the sender to download these files.
                    </DialogDescription>
                </DialogHeader>
                <form id="submitPasswordForm" @submit="onSubmit" class="flex flex-col gap-4 py-4">
                    <TextControlField name="password" type="password"></TextControlField>
                    <p v-if="isPasswordIncorrect" class="text-destructive text-xs">
                        The password is incorrect
                    </p>
                </form>
                <DialogFooter class="flex justify-between sm:justify-between">
                    <Button variant="outline" @click="showPasswordDialog = false">Cancel</Button>
                    <Button
                        variant="yellow"
                        form="submitPasswordForm"
                        :disabled="isSubmitting || isDownloading || isTurnstileTokenLoaded">
                        <span v-if="isSubmitting || isDownloading" class="mr-2">
                            <svg
                                class="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24">
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"></circle>
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </span>
                        Download
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        <!-- Turnstile -->
        <div v-if="isTurnstileLoaded && turnstileCheckEnabled" class="flex justify-center">
            <VueTurnstile
                ref="turnstile"
                :site-key="turnstileSiteKey ?? ''"
                v-model="turnstileToken"></VueTurnstile>
        </div>
    </div>
</template>

<script setup lang="ts">
import { TextControlField } from '@/website/components/core/forms/text-control'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useDownloadFile } from '@/website/composables/use-download-file'
import { useForm } from 'vee-validate'
import { computed, ref } from 'vue'
import { object, string } from 'yup'
import { CONSTANTS } from '@/constants'
import VueTurnstile from 'vue-turnstile'
import { useCloudflareTurnstile } from '@/apps/website/composables/useCloudflareTurnstile'

interface File {
    name: string
    extension: string
    sizeLabel: string
    id: string
}

interface FileSharingPasswordFormFields {
    password: string
}

interface Props {
    shortUrlId: string
    isDownloadRequirePassword?: boolean
    files?: File[]
    turnstileCheckEnabled?: boolean
    turnstileSiteKey?: string
}

const props = withDefaults(defineProps<Props>(), {
    files: () => [],
})

const showPasswordDialog = ref(false)
const isPasswordRecorded = ref(false)
const isPasswordIncorrect = ref(false)
const isDownloadStarted = ref(false)
const singleFileIdToDownload = ref('')
const password = ref('')
const turnstileToken = ref('')
const turnstile = ref<InstanceType<typeof VueTurnstile>>()

const { load, isTurnstileLoaded } = useCloudflareTurnstile()

const isTurnstileTokenLoaded = computed(() => {
    if (!props.turnstileCheckEnabled) return false

    return !turnstileToken.value
})

const { handleSubmit, isSubmitting } = useForm<FileSharingPasswordFormFields>({
    validationSchema: object({
        password: string().required('Password is required'),
    }),
})

const onSubmit = handleSubmit(async (values) => {
    if (!values.password) {
        return
    }

    password.value = values.password
    isPasswordIncorrect.value = false

    // Call the API to unlock the file sharing link with the password
    // If successful, proceed to download the file
    await download()
    turnstile.value?.reset()

    if (statusCode.value === 200) {
        isPasswordRecorded.value = true
        showPasswordDialog.value = false
        isDownloadStarted.value = true
    } else if (statusCode.value === 403) {
        isPasswordIncorrect.value = true
    }
})

// For password-protected downloads, we'll use the composable
const { download, isDownloading, statusCode } = useDownloadFile(
    computed(() => {
        let url = `/api/v1/short-urls/${props.shortUrlId}/file-sharing/downloads`

        if (singleFileIdToDownload.value) {
            url += `?mediaIds=${singleFileIdToDownload.value}`
        }

        return url
    }),
    {
        async beforeFetch({ options }) {
            options.headers = {
                ...options.headers,
                'x-files-pwd': password.value,
                [CONSTANTS.CF_TURNSTILE_HTTP_HEADER]: turnstileToken.value,
            }

            return {
                options,
            }
        },
    },
)

const downloadSingleFile = (fileId: string) => {
    singleFileIdToDownload.value = fileId

    if (!props.isDownloadRequirePassword || isPasswordRecorded.value) {
        download()
        turnstile.value?.reset()
    } else {
        showPasswordDialog.value = true
    }
}

const downloadAllFiles = () => {
    singleFileIdToDownload.value = ''

    if (!props.isDownloadRequirePassword || isPasswordRecorded.value) {
        download()
        turnstile.value?.reset()
    } else {
        showPasswordDialog.value = true
    }
}

load()
</script>

<style scoped></style>
